/**
 * Sezione Cambio Password
 */
import React, { useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordSection = ({ t }) => {
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Le password non corrispondono');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password aggiornata');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nel cambio password'));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Card data-testid="password-section">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Lock className="h-5 w-5" />
          {t('settings.changePassword')}
        </CardTitle>
        <CardDescription>
          {t('settings.changePasswordDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label>{t('settings.currentPassword')}</Label>
            <div className="relative">
              <Input 
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>{t('settings.newPassword')}</Label>
            <div className="relative">
              <Input 
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Minimo 8 caratteri</p>
          </div>
          
          <div className="space-y-2">
            <Label>{t('settings.confirmPassword')}</Label>
            <div className="relative">
              <Input 
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <Button 
            onClick={handleChangePassword}
            disabled={changingPassword}
            data-testid="change-password-btn"
          >
            {changingPassword ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Aggiornamento...
              </>
            ) : (
              t('settings.updatePassword')
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PasswordSection;
